import { useState } from "react";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import confetti from "canvas-confetti";
import toast from "react-hot-toast";
import Footer from "../../components/Footer";
import { CHURCH_INFO } from "../../utils/churchInfo";
import { PAYPAL_CLIENT_ID, GIVING_PRESET_AMOUNTS } from "../../constants/giving";
import { usePageMeta } from "../../utils/usePageMeta";
import "../Home/Styles.css";

const supportItems = [
  {
    label: "Email",
    value: CHURCH_INFO.email,
    href: `mailto:${CHURCH_INFO.email}`,
  },
  {
    label: "Website",
    value: CHURCH_INFO.websiteLabel,
    href: CHURCH_INFO.websiteUrl,
  },
  {
    label: "Privacy",
    value: "Privacy Policy",
    href: "/privacy",
  },
  {
    label: "Support",
    value: "App Support",
    href: "/support",
  },
];

const GivingPage = () => {
  usePageMeta(
    "Give | Trinity Christian Church",
    "Give with purpose at Trinity Christian Church through a simple, secure, mobile-friendly online giving experience."
  );

  const [titheAmount, setTitheAmount] = useState<string>("");
  const [customAmount, setCustomAmount] = useState("");
  const [showPaypalBtns, setShowPaypalBtns] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);

  const showConfetti = () => {
    const colors = ["#53a624", "#19369c", "#c9a15c"];

    confetti({
      particleCount: 60,
      angle: 60,
      spread: 55,
      origin: { x: 0 },
      colors,
    });
    confetti({
      particleCount: 60,
      angle: 120,
      spread: 55,
      origin: { x: 1 },
      colors,
    });
  };

  const handlePresetSelect = (amount: string) => {
    setTitheAmount(amount);
    setCustomAmount("");
    setShowPaypalBtns(true);
  };

  const handleCustomAmountChange = (value: string) => {
    setTitheAmount("");
    setCustomAmount(value);
    setShowPaypalBtns(false);
  };

  const handleCustomAmountConfirm = () => {
    if (customAmount && Number.parseFloat(customAmount) > 0) {
      setTitheAmount(customAmount);
      setShowPaypalBtns(true);
    }
  };

  const resetGivingFlow = () => {
    setTitheAmount("");
    setCustomAmount("");
    setShowPaypalBtns(false);
  };

  return (
    <main className="min-h-screen bg-white text-slate-900">
      <header className="w-full border-b border-slate-200 bg-white px-6 py-4 shadow-sm">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-4">
          <div className="min-w-0">
            <p
              className="text-[11px] text-slate-500"
              style={{ letterSpacing: "0.65em", opacity: 0.95 }}
            >
              T R I N I T Y &nbsp; C H R I S T I A N &nbsp; C H U R C H
            </p>
            <h1 className="mt-2 text-3xl archivo-black-regular text-tcc-blue">
              Give
            </h1>
          </div>

          <div className="flex flex-wrap gap-3">
            <a
              href="/"
              className="rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 hover:text-tcc-blue"
            >
              Back Home
            </a>
            <a
              href="/support"
              className="rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 hover:text-tcc-blue"
            >
              App Support
            </a>
          </div>
        </div>
      </header>

      <section className="w-full bg-gradient-to-br from-tcc-blue to-tcc-green px-6 py-16 md:px-8 md:py-20">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="text-4xl archivo-black-regular font-bold text-white mb-6 mobile:text-2xl">
            Give <i className="anton-regular-italic">Online</i>
          </h2>
          <p className="mx-auto max-w-3xl text-lg text-white opacity-90">
            Your generosity helps us continue our mission to serve the community
            and spread God&apos;s love. Thank you for your faithful support and
            partnership in ministry.
          </p>

          <div className="mb-8 mt-8 flex flex-wrap items-center justify-center gap-3">
            <a
              href="/privacy"
              className="rounded-full bg-white px-6 py-3 font-semibold text-tcc-blue transition hover:bg-opacity-90"
            >
              Privacy Policy
            </a>
            <a
              href="/support"
              className="rounded-full border border-white/30 bg-white/10 px-6 py-3 font-semibold text-white transition hover:bg-white/15"
            >
              Need Donation Help?
            </a>
          </div>

          <div id="giving-card" className="bg-white rounded-lg p-8 shadow-xl text-left">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-tcc-blue/70">
                  Official Giving
                </p>
                <h3 className="mt-2 text-3xl archivo-black-regular font-bold text-tcc-blue mobile:text-2xl">
                  Choose Your Gift
                </h3>
              </div>
              <div className="rounded-full bg-tcc-green/10 px-4 py-2 text-sm font-semibold text-tcc-green">
                Secure Checkout
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-3">
              {GIVING_PRESET_AMOUNTS.map((amount, index) => (
                <button
                  key={amount}
                  type="button"
                  aria-pressed={titheAmount === amount}
                  onClick={() => handlePresetSelect(amount)}
                  className={`text-center p-4 rounded-lg border-2 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                    titheAmount === amount
                      ? index === 0
                        ? "border-tcc-blue bg-tcc-blue bg-opacity-10 focus-visible:ring-tcc-blue"
                        : "border-tcc-green bg-tcc-green bg-opacity-10 focus-visible:ring-tcc-green"
                      : index === 0
                        ? "border-gray-200 hover:border-tcc-blue focus-visible:ring-tcc-blue"
                        : "border-gray-200 hover:border-tcc-green focus-visible:ring-tcc-green"
                  }`}
                >
                  <div
                    className={`text-3xl font-bold mb-2 ${
                      index === 0 ? "text-tcc-blue" : "text-tcc-green"
                    }`}
                  >
                    ${amount}
                  </div>
                  <div className="text-gray-600 text-sm">One-Time Gift</div>
                </button>
              ))}

              <div className="text-center p-4 rounded-lg border-2 border-gray-200">
                <div className="text-3xl font-bold text-tcc-brown mb-2">Custom</div>
                <label htmlFor="custom-giving-amount" className="sr-only">
                  Enter custom giving amount
                </label>
                <input
                  id="custom-giving-amount"
                  type="number"
                  inputMode="decimal"
                  min="1"
                  step="0.01"
                  placeholder="Enter amount"
                  value={customAmount}
                  onChange={(e) => handleCustomAmountChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleCustomAmountConfirm();
                    }
                  }}
                  className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg text-center focus:outline-none focus:border-tcc-brown focus-visible:ring-2 focus-visible:ring-tcc-brown focus-visible:ring-offset-2"
                />
                {customAmount && (
                  <button
                    type="button"
                    onClick={handleCustomAmountConfirm}
                    className="w-full mt-2 bg-tcc-brown text-white py-2 px-4 rounded-lg font-semibold hover:bg-opacity-90 transition-all text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-tcc-brown focus-visible:ring-offset-2"
                  >
                    Confirm ${customAmount}
                  </button>
                )}
              </div>
            </div>

            {showPaypalBtns && titheAmount && (
              <PayPalScriptProvider
                options={{
                  clientId: PAYPAL_CLIENT_ID,
                  currency: "USD",
                }}
              >
                <div className="max-w-md mx-auto mb-4">
                  <PayPalButtons
                    style={{
                      layout: "vertical",
                      label: "paypal",
                    }}
                    createOrder={(data, actions) => {
                      return actions.order.create({
                        intent: "CAPTURE",
                        purchase_units: [
                          {
                            amount: {
                              currency_code: "USD",
                              value: titheAmount,
                            },
                          },
                        ],
                      });
                    }}
                    onApprove={(data, actions) => {
                      return actions.order!.capture().then(() => {
                        showConfetti();
                        setShowThankYouModal(true);
                        resetGivingFlow();
                      });
                    }}
                    onError={(err) => {
                      console.error("PayPal Error:", err);
                      toast.error("Payment failed. Please try again.");
                    }}
                    onCancel={() => {
                      resetGivingFlow();
                      toast("Payment cancelled");
                    }}
                  />
                </div>
              </PayPalScriptProvider>
            )}

            <div className="mt-6 grid grid-cols-1 gap-4 border-t border-gray-100 pt-6 md:grid-cols-2">
              {supportItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  target={item.href.startsWith("http") ? "_blank" : undefined}
                  rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-4 transition hover:border-gray-200"
                >
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">
                    {item.label}
                  </p>
                  <p className="mt-2 text-base font-semibold text-tcc-blue break-words">
                    {item.value}
                  </p>
                </a>
              ))}
            </div>

            <p className="text-gray-500 text-sm mt-6 text-center">
              All donations are tax-deductible. You will receive a receipt via email.
            </p>
          </div>
        </div>
      </section>

      <Footer
        homePageData={{
          address: `${CHURCH_INFO.addressLine1}, ${CHURCH_INFO.addressLine2}`,
          phone: CHURCH_INFO.phoneDisplay,
        }}
      />

      {showThankYouModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(15, 23, 42, 0.78)" }}
          onClick={() => setShowThankYouModal(false)}
        >
          <div
            className="w-full max-w-md rounded-[2rem] bg-white p-8 text-center shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-tcc-green/[0.15] text-3xl text-tcc-green">
              Thank
            </div>
            <h2 className="mt-6 text-3xl text-tcc-blue archivo-black-regular">
              Thank You
            </h2>
            <p className="mt-4 text-base leading-8 text-slate-700">
              Thank you for your generous contribution and partnership in
              ministry.
            </p>
            <button
              type="button"
              onClick={() => setShowThankYouModal(false)}
              className="mt-8 w-full rounded-xl bg-tcc-blue px-6 py-3 font-semibold text-white transition hover:bg-opacity-90"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </main>
  );
};

export default GivingPage;
